import { Container, Sprite, Text } from '../../odie';

import { config } from '../../game/defs/config';
import { BlockDef } from '../../game/defs/map';
import { BasicButton } from '../../lib/ui/BasicButton';
import { createBlockView } from '../support/blockViewFactory';
import { BlockOptionDef, blockPropsMap } from '../../game/defs/block';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  CountSize,
  blockOptionFromCountSize,
  blockOptionToCountSize,
} from '../../game/util/blockOption';
import { getGoalsPath } from '../../game/util/assets';
import { getGoalMap } from '../../game/defs/goal';

// types
//-----------------------------------------------------------------------------
export type BlockButtonType = 'block' | 'goal' | 'clear' | 'tile';

export type BlockButtonOptions = {
  type: BlockButtonType;
  def: BlockDef;
  key?: string;
  disableOptions?: boolean;
};

// constants
//-----------------------------------------------------------------------------
const manifest = {
  button: 'assets/ui/match2/editor/buttons/button.blockmenu.png',
  buttonSelected:
    'assets/ui/match2/editor/buttons/button.blockmenu.selected.png',
  clear: 'assets/ui/match2/editor/icons/icon.blockmenu.clear.png',
  tile: 'assets/ui/match2/editor/icons/icon.blockmenu.tile.png',
};

function deepCompare(a: Record<string, any>, b: Record<string, any>) {
  for (const key in a) {
    if (Object.hasOwn(a, key)) {
      const prop = a[key];
      if (typeof prop === 'object') {
        if (!deepCompare(prop, b[key])) return false;
      } else {
        if (prop !== b[key]) return false;
      }
    }
  }
  return true;
}

/*
    puzzle editor block menu button
*/
export class BlockButton extends BasicButton {
  // private
  //-------------------------------------------------------------------------
  //private _text: BasicText;
  // input
  private readonly _type: BlockButtonType;
  private _def!: BlockDef;
  // scene
  private _icon?: Container;
  private _optionLabel: Text;
  private _optionContainer: Container;
  // overrides
  private _optionsOverride: BlockOptionDef[] | null = null;
  private _indexOverride: number | null = null;

  // properties
  //-------------------------------------------------------------------------
  public get type(): BlockButtonType {
    return this._type;
  }

  public get def(): BlockDef {
    return this._def;
  }

  public set def(def: BlockDef) {
    this._def = def;

    // update icon
    this._updateIcon();
  }

  public get options(): BlockOptionDef[] {
    const props = blockPropsMap[this.def.id];
    return this._optionsOverride ?? props?.options ?? [];
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: BlockButtonOptions) {
    super({
      up: manifest.button,
      selected: manifest.buttonSelected,
    });

    this.width = 76;
    this.height = 76;

    this._optionLabel = new Text('', {
      font: 'Title',
      fontSize: 16,
      align: 'center',
    });
    this._optionLabel.x = this.width * 0.5 - 2;
    this._optionLabel.y = this.height - 25;
    this.addChild(this._optionLabel);

    this._optionContainer = new Container();
    this.addChild(this._optionContainer);

    // set fields
    this._type = options.type;
    this.def = options.def;

    // set key
    if (options.key) {
      new LangBitmapFontTextView({
        superview: this.view,
        width: this.width,
        height: this.height,
        align: 'right',
        color: '#ffffff',
        font: 'Title',
        size: 20,
        localeText: () => options.key?.toUpperCase() ?? '',
      });
    }
  }

  public static assets(): string[] {
    return Object.values(manifest);
  }

  public setOptionsOverride(excluded?: BlockOptionDef[]) {
    if (this.options.length < 2) return;
    this._optionsOverride = excluded ? [...excluded] : null;
    this._indexOverride = excluded ? 0 : null;
    let index = 0;
    if (this._optionsOverride && this._indexOverride !== null) {
      index = this.getDefaultOptionsIndex();
    }
    this.updateIndex(index);
  }

  public nextOption() {
    if (this.options.length < 2) return;
    let index;
    if (this._optionsOverride) {
      this._indexOverride =
        ((this._indexOverride ?? 0) + 1) % this.options.length;
      index = this.getDefaultOptionsIndex();
    } else {
      index = ((this.def.option ?? 0) + 1) % this.options.length;
    }
    this.updateIndex(index);
  }

  // private: updaters
  //-------------------------------------------------------------------------
  private updateIndex(index: number) {
    this.def = {
      ...this.def,
      option: index,
    };
  }

  private _updateIcon() {
    let icon: Container;

    this.width = 76;
    this.height = 76;

    // remove last icon
    if (this._icon) this.content.removeChild(this._icon);

    // create icon
    if (this._type === 'block') {
      icon = createBlockView(this.def);
      this.fitIcon(icon);
      icon.pivotX = 0;
      icon.pivotY = 0;
      icon.x = this.width * 0.5;
      icon.y = this.height * 0.5;

      // Dog block is a special case, probably because it is an animated sprite
      if (this.def.id === 'dog') {
        icon.children[0].x = 0;
        icon.children[0].y = 0;
      }
    } else if (this._type === 'goal') {
      const image = getGoalMap()[this.def.id];
      const imagePath = `${getGoalsPath()}${image}`;
      icon = Sprite.from(imagePath);
      icon.width = config.tile.size;
      icon.height = config.tile.size;
    } else {
      icon = Sprite.from(manifest[this._type]);
      icon.width = config.tile.size;
      icon.height = config.tile.size;
      icon.scaleX = 0.8;
      icon.scaleY = 0.8;
      icon.anchorX = 0.5;
      icon.anchorY = 0.5;
      icon.x = this.width * 0.5;
      icon.y = this.height * 0.5;
    }

    this._updateOption();

    this._icon = icon;
    this.content.addChild(icon);
  }

  private _updateOption() {
    const props = blockPropsMap[this.def.id];
    if (!props) return;
    if (props.noEditorOptions) return;
    if (props.optionType === 'countSize') {
      this._updateOptionCountSize();
    } else {
      this._updateOptionList();
    }
    this.addChild(this._optionLabel);
  }

  private _updateOptionList() {
    const options = this.options;
    const current = (this._indexOverride ?? this.def.option ?? 0) + 1;
    const total = options.length;
    this._optionLabel.text = options.length ? `${current}/${total}` : '';

    this.addSpawnBlocksIcons();
  }

  private addSpawnBlocksIcons() {
    this._optionContainer.view.removeAllSubviews();

    const current = this._indexOverride ?? this.def.option ?? 0;
    const selectedOption = this.options[current];

    // No selected option.
    if (!selectedOption) return;

    // No spawn blocks in selected option.
    if (!('spawnBlocks' in selectedOption)) return;

    // Offset the option label to the right. So the icons can fit.
    this._optionLabel.x = this.width * 0.5 + 15;

    // Instantiate miniatures of the blocks that can be spawned.
    const { spawnBlocks } = selectedOption;
    const instantiated: string[] = [];
    spawnBlocks.forEach((blockId, index) => {
      // Handle repeated blocks.
      if (instantiated.some((id) => blockId.includes(id))) return;

      // Rockets should be treated as a single block.
      if (blockId.includes('rocket')) {
        instantiated.push('rocket');
      }
      if (blockId.includes('cube')) {
        instantiated.push('cube');
        blockId = 'cubeBlue';
      } else {
        instantiated.push(blockId);
      }
      const icon = createBlockView({ id: blockId });
      icon.scaleX = 0.3;
      icon.scaleY = 0.3;
      icon.x = 11 + index * 10;
      icon.y = this.height - 11;
      this._optionContainer.addChild(icon);
    });
  }

  private _updateOptionCountSize() {
    const props = blockPropsMap[this.def.id];
    const defaultOption = blockOptionFromCountSize(props as CountSize);
    const option = this.def.option || defaultOption;
    const countSize = blockOptionToCountSize(option) ?? { count: 0, ...props };
    this._optionLabel.text = `${countSize.count} ${countSize.width}x${countSize.height}`;
  }

  private fitIcon(icon: Container) {
    if (!icon.width || !icon.height) {
      const props = blockPropsMap[this.def.id];
      const optionDef = props.options?.[this.def.option ?? 0] as {
        width?: number;
        height?: number;
      };
      icon.scaleX = 0.8 / (optionDef?.width ?? props.width ?? 1);
      icon.scaleY = 0.8 / (optionDef?.height ?? props.height ?? 1);
    } else if (icon.width > icon.height) {
      icon.scaleX = 0.8 / Math.floor(icon.width / config.tile.size);
      icon.scaleY = icon.scaleX;
    } else {
      icon.scaleY = 0.8 / Math.floor(icon.height / config.tile.size);
      icon.scaleX = icon.scaleY;
    }
  }

  private getDefaultOptionsIndex() {
    if (!this._optionsOverride) return 0;
    const option = this._optionsOverride[this._indexOverride ?? 0];
    const props = blockPropsMap[this.def.id];
    return props?.options?.findIndex((o) => deepCompare(o, option)) ?? 0;
  }
}
